import type { DeviceProfile } from "./device-profiles";
import {
  type DeviceProfileId,
  resolveDeviceProfile,
} from "./device-profiles";
import type { LuckContentMode } from "./content-modes";
import type { LuckPaperMode } from "./print-modes";

export const LUCK_PRINTER_DEVICE_ID_KEY = "luck_printer_device_id";
export const LUCK_PRINTER_DEVICE_NAME_KEY = "luck_printer_device_name";
export const LUCK_PRINTER_DENSITY_KEY = "luck_printer_density";
export const LUCK_PRINTER_DEVICE_PROFILE_KEY = "luck_printer_device_profile";
export const LUCK_PRINTER_FEED_DOTS_KEY = "luck_printer_feed_dots";
export const LUCK_PRINTER_CONTENT_MODE_KEY = "luck_printer_content_mode";
export const LUCK_PRINTER_PAPER_MODE_KEY = "luck_printer_paper_mode";
/** @deprecated migrated to LUCK_PRINTER_PAPER_MODE_KEY */
export const LUCK_PRINTER_PRINT_MODE_KEY = "luck_printer_print_mode";

export type LuckPrinterDensity = 0 | 1 | 2;

export type { DeviceProfileId };
export type { LuckContentMode };
export type { LuckPaperMode };
/** @deprecated Use LuckPaperMode */
export type LuckPrintMode = LuckPaperMode;

function readLegacyPaperMode(): LuckPaperMode | null {
  const raw = localStorage.getItem(LUCK_PRINTER_PRINT_MODE_KEY);
  if (raw === "continuous" || raw === "label-gap" || raw === "tattoo") {
    return raw;
  }
  return null;
}

export function getSavedLuckPrinterDevice(): {
  id: string;
  name: string;
} | null {
  if (typeof localStorage === "undefined") return null;
  const id = localStorage.getItem(LUCK_PRINTER_DEVICE_ID_KEY);
  if (!id) return null;
  return {
    id,
    name: localStorage.getItem(LUCK_PRINTER_DEVICE_NAME_KEY) ?? "Printer",
  };
}

export function saveLuckPrinterDevice(id: string, name: string) {
  localStorage.setItem(LUCK_PRINTER_DEVICE_ID_KEY, id);
  localStorage.setItem(LUCK_PRINTER_DEVICE_NAME_KEY, name);
}

export function clearSavedLuckPrinterDevice() {
  localStorage.removeItem(LUCK_PRINTER_DEVICE_ID_KEY);
  localStorage.removeItem(LUCK_PRINTER_DEVICE_NAME_KEY);
}

export function getSavedLuckPrinterDensity(): LuckPrinterDensity {
  const raw = localStorage.getItem(LUCK_PRINTER_DENSITY_KEY);
  if (raw === "0" || raw === "1" || raw === "2") {
    return Number(raw) as LuckPrinterDensity;
  }
  return 1;
}

export function saveLuckPrinterDensity(density: LuckPrinterDensity) {
  localStorage.setItem(LUCK_PRINTER_DENSITY_KEY, String(density));
}

export function getSavedLuckPrinterDeviceProfile(): DeviceProfileId {
  const raw = localStorage.getItem(LUCK_PRINTER_DEVICE_PROFILE_KEY);
  if (raw === "luck-d1" || raw === "luck-dp-l1s" || raw === "generic-luck") {
    return raw;
  }
  return "generic-luck";
}

export function saveLuckPrinterDeviceProfile(id: DeviceProfileId) {
  localStorage.setItem(LUCK_PRINTER_DEVICE_PROFILE_KEY, id);
}

export function getSavedLuckPrinterFeedDots(): number {
  const profile = resolveDeviceProfile(getSavedLuckPrinterDeviceProfile());
  const raw = localStorage.getItem(LUCK_PRINTER_FEED_DOTS_KEY);
  if (!raw) return profile.defaultFeedDots;

  const value = Number(raw);
  if (!Number.isFinite(value)) return profile.defaultFeedDots;
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function saveLuckPrinterFeedDots(dots: number) {
  const value = Math.max(0, Math.min(255, Math.round(dots)));
  localStorage.setItem(LUCK_PRINTER_FEED_DOTS_KEY, String(value));
}

export function getSavedLuckPrinterContentMode(): LuckContentMode {
  const raw = localStorage.getItem(LUCK_PRINTER_CONTENT_MODE_KEY);
  if (raw === "text" || raw === "image") return raw;
  return "image";
}

export function saveLuckPrinterContentMode(mode: LuckContentMode) {
  localStorage.setItem(LUCK_PRINTER_CONTENT_MODE_KEY, mode);
}

export function getSavedLuckPrinterPaperMode(): LuckPaperMode {
  const raw = localStorage.getItem(LUCK_PRINTER_PAPER_MODE_KEY);
  if (raw === "continuous" || raw === "label-gap" || raw === "tattoo") {
    return raw;
  }
  const legacy = readLegacyPaperMode();
  if (legacy) return legacy;
  return resolveDeviceProfile(getSavedLuckPrinterDeviceProfile())
    .suggestedPaperMode;
}

export function saveLuckPrinterPaperMode(mode: LuckPaperMode) {
  localStorage.setItem(LUCK_PRINTER_PAPER_MODE_KEY, mode);
  localStorage.setItem(LUCK_PRINTER_PRINT_MODE_KEY, mode);
}

/** @deprecated Use getSavedLuckPrinterPaperMode */
export function getSavedLuckPrinterPrintMode(): LuckPaperMode {
  return getSavedLuckPrinterPaperMode();
}

/** @deprecated Use saveLuckPrinterPaperMode */
export function saveLuckPrinterPrintMode(mode: LuckPaperMode) {
  saveLuckPrinterPaperMode(mode);
}

export function getActiveLuckPrinterProfile(): DeviceProfile {
  return resolveDeviceProfile(getSavedLuckPrinterDeviceProfile());
}
